import type { ControlRoomJob } from "./service";

/**
 * The pure "which bucket does this job fall into" logic behind Control Room's rail sections
 * (`ControlRoomScreen.tsx`'s `tabForJob`/`RAIL_SECTION_ORDER`) — pulled out into its own module
 * (rather than staying private to that screen) so `SpotterHeader.tsx`'s Control Room tab badge
 * (`useControlRoomNeedsAttention`, `controlRoom/service.ts`) can ask "does anything need
 * attention?" using the EXACT SAME precedence the rail itself uses, without a second, drifting
 * copy of it and without `SpotterHeader.tsx` importing from `ControlRoomScreen.tsx` (which
 * already imports `SpotterHeader` — a cycle). `ControlRoomScreen.tsx` re-exports the names its
 * own tests already import from it, so nothing there had to change shape.
 */

/**
 * The one thing about a job that decides both its status pill (`stageDisplay`) and its rail
 * section (`tabForJob`), so the two never drift apart. Precedence:
 *  1. An open anchor-job review (`hasReview && reviewNeedsInput`) wins over everything, `done`
 *     included: the pipeline finishing says nothing about whether that separate review was answered.
 *  2. `done` is final. A done job with failed items, or one that reported an error after finishing
 *     (`hadLateError`), is `warning`, never red.
 *  3. `error`, then the `needsInput` latch.
 *  4. `stalled`: still running but silent for `STALLED_JOB_THRESHOLD_MS`. Never shown as failed.
 *  5. Otherwise the job is moving through its ranked stages.
 * `hidden` (`tabForJob`) is decided before this runs.
 */
export type PrimaryJobState =
  | "needsReview"
  | "warning"
  | "done"
  | "error"
  | "needsInput"
  | "stalled"
  | "stage";

export function primaryJobState(
  job: ControlRoomJob,
  now: number = Date.now()
): PrimaryJobState {
  if (job.hasReview && job.reviewNeedsInput) {
    return "needsReview";
  }
  if (job.latestStage === "done") {
    return job.latestStatus === "warning" || job.hadLateError
      ? "warning"
      : "done";
  }
  if (job.latestStatus === "error") {
    return "error";
  }
  if (job.needsInput) {
    return "needsInput";
  }
  if (isStalled(job, now)) {
    return "stalled";
  }
  return "stage";
}

/** How long a running job can go without a push before it reads as stalled. */
export const STALLED_JOB_THRESHOLD_MS = 6 * 60 * 60 * 1000;

/** No push for `STALLED_JOB_THRESHOLD_MS`. Only meaningful for a job that isn't done or failed,
 *  which `primaryJobState` checks first. */
export function isStalled(
  job: ControlRoomJob,
  now: number = Date.now()
): boolean {
  return now - new Date(job.lastUpdatedAt).getTime() > STALLED_JOB_THRESHOLD_MS;
}

/** Rail section a job falls into (Monday 12921148830, reformatted into the rail by design A4) —
 *  no dedicated "error" section: an errored job (not yet done) still counts as "still processing"
 *  here, same as it isn't its own row in `MAIN_STAGES` either. */
export type ControlRoomTabKey =
  | "all"
  | "needsYou"
  | "processing"
  | "done"
  | "hidden";

export function tabForJob(
  job: ControlRoomJob
): Exclude<ControlRoomTabKey, "all"> {
  // A hidden job lands under `hidden` and nowhere else, even with an open review.
  if (job.dismissed) {
    return "hidden";
  }
  const state = primaryJobState(job);
  if (state === "needsReview" || state === "needsInput") {
    return "needsYou";
  }
  if (state === "done" || state === "warning") {
    return "done";
  }
  return "processing";
}

export type HideButtonMode = "hide" | "unhide";

/** The rail row's hide/unhide control mode, from the bucket the row is in. */
export function hideButtonMode(job: ControlRoomJob): HideButtonMode {
  return tabForJob(job) === "hidden" ? "unhide" : "hide";
}

/** A run that stopped before finishing. Independent of `primaryJobState`, where an open review
 *  outranks the error: the failure report still wins the right pane. */
export function isFailedJob(job: ControlRoomJob): boolean {
  return job.latestStatus === "error" && job.latestStage !== "done";
}

/** A done run that reported an error afterwards. Still done, shown as a warning. */
export function isLateErrorJob(job: ControlRoomJob): boolean {
  return job.latestStage === "done" && !!job.hadLateError;
}

/** A job whose failure report the right pane can show. */
export function hasFailureReport(job: ControlRoomJob): boolean {
  return isFailedJob(job) || isLateErrorJob(job);
}

/** When the run failed: the report's own time, else when the error first arrived. */
export function failureTime(job: ControlRoomJob): string {
  return job.report?.failedAt ?? job.erroredAt ?? job.lastUpdatedAt;
}

/** How long a failed job without a report shows "generating report" before settling on its note. */
export const REPORT_PENDING_WINDOW_MS = 10 * 60 * 1000;

export function isReportPending(job: ControlRoomJob, now: number): boolean {
  return (
    hasFailureReport(job) &&
    !job.report &&
    now - new Date(failureTime(job)).getTime() < REPORT_PENDING_WINDOW_MS
  );
}

export type RightPaneKind = "nothing" | "review" | "failure";

/** `?view=review` is the failure panel's link back to a failed job's play confirmations. A job
 *  missing from the list (still loading, or deep-linked) keeps the review panel, as before. */
export function rightPaneKind(
  jobId: string | undefined,
  job: ControlRoomJob | undefined,
  view: string | null
): RightPaneKind {
  if (!jobId) {
    return "nothing";
  }
  if (job && hasFailureReport(job) && !(view === "review" && job.hasReview)) {
    return "failure";
  }
  return "review";
}

/** The rail row's one-line note: a failure's report summary or late-error note, else n8n's note. */
export function railNoteFor(job: ControlRoomJob): string {
  if (isLateErrorJob(job)) {
    return job.report?.summary || job.lateErrorNote || job.note;
  }
  return (isFailedJob(job) && job.report?.summary) || job.note;
}
